// Client for the tariff (فهرست بها) API. Mirrors designApi's auth pattern: the
// Bearer is the user's Supabase access token, and Express does the role check.
//
// supabaseClient is imported LAZILY, inside authHeaders. It builds a real client
// at module scope and throws when the anon key is absent, so a static import here
// would make merely importing a display helper (money/moneyShort, used by the
// design panel and its tests) construct a Supabase client. Deferring it keeps
// this module side-effect-free at import time.

async function authHeaders(json = false) {
  const { supabase } = await import('./supabaseClient');
  const { data: { session } } = await supabase.auth.getSession();
  const headers = json ? { 'Content-Type': 'application/json' } : {};
  if (session && session.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

async function call(method, path, body) {
  const headers = await authHeaders(!!body);
  let resp;
  try {
    resp = await fetch(`/api/tariffs${path}`, {
      method, headers, body: body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    return { error: 'خطای شبکه.' };
  }
  const data = await resp.json().catch(() => null);
  if (!resp.ok) return { ...data, error: (data && data.error) || 'دریافت یا ذخیره انجام نشد.', httpStatus: resp.status };
  return data || {};
}

export const listVersions = () => call('GET', '/versions');
export const listCopySources = () => call('GET', '/copy-sources');
export const createVersion = (body) => call('POST', '/versions', body);
export const updateVersion = (id, body) => call('PATCH', `/versions/${id}`, body);
export const deleteVersion = (id) => call('DELETE', `/versions/${id}`);
export const listVersionItems = (id) => call('GET', `/versions/${id}/items`);
export const savePrices = (id, prices) => call('PUT', `/versions/${id}/prices`, { prices });
export const deletePrice = (id, itemId) => call('DELETE', `/versions/${id}/prices/${itemId}`);
export const createItem = (body) => call('POST', '/items', body);
export const updateItem = (id, body) => call('PATCH', `/items/${id}`, body);
export const itemHistory = (code) => call('GET', `/items/${encodeURIComponent(code)}/history`);
export const listQtySources = () => call('GET', '/qty-sources');
export const priceProject = (projectId, body) => call('POST', `/price/${projectId}`, body || {});

// ---------------------------------------------------------------------------
// display helpers
// ---------------------------------------------------------------------------

export const CURRENCY_FA = { IRR: 'ریال', IRT: 'تومان' };

// Grouped thousands in Latin digits. Persian digits are deliberately NOT used for
// money: a 12-digit ریال figure is materially harder to scan in Persian numerals,
// and these are read against an Excel that prints Latin.
//
// null/undefined render as '-', NOT '0'. An item with no price row in the chosen
// book is UNPRICED, and Number(null) === 0 would present that as free.
export function money(value) {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// A ریال total on a real city design runs to ~10^11, which is unreadable raw.
// Fold to میلیون / میلیارد / همت for the headline figures only; the itemised
// table always shows the exact number.
export function moneyShort(value, currency = 'IRR') {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return `۰ ${CURRENCY_FA[currency] || ''}`.trim();
  const unit = CURRENCY_FA[currency] || '';
  const scales = [
    [1e12, 'هزار میلیارد'],
    [1e9, 'میلیارد'],
    [1e6, 'میلیون'],
    [1e3, 'هزار'],
  ];
  for (const [size, label] of scales) {
    if (Math.abs(n) >= size) {
      const v = n / size;
      return `${v.toLocaleString('en-US', { maximumFractionDigits: v < 10 ? 2 : 1 })} ${label} ${unit}`.trim();
    }
  }
  return `${money(n)} ${unit}`.trim();
}

// Gregorian ISO date -> Jalali, because a tariff is referred to by its Persian
// year ("تعرفهٔ ۱۴۰۵"). Intl handles the conversion; no date library needed.
//
// effective_from is a DATE column, so it arrives as a bare 'YYYY-MM-DD'. new Date
// on that string parses as UTC MIDNIGHT while Intl formats in the viewer's local
// zone, so west of UTC the day slips backwards: the 1405 book, effective
// 2026-03-21 (= 1 Farvardin 1405), rendered as ۱۴۰۴/۱۲/۲۹ - the wrong YEAR, on
// the one field whose whole job is naming the year. Build a LOCAL date from the
// parts so a calendar date stays the date it was written as, everywhere.
export function toJalali(iso) {
  if (!iso) return '-';
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  const d = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

export const STATUS_FA = { draft: 'پیش‌نویس', active: 'منتشرشده', archived: 'بایگانی' };
